import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Loader } from '@hologram-dimension/loader';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useUsageData from 'api/legacy/usage/useUsageData';
import useSimBilling from 'api/sims/useSimBilling';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import SimRecentDataSessionsChartPanel from 'components/SimRecentDataSessionsChartPanel/SimRecentDataSessionsChartPanel';
import SimSessionsPanel from 'components/SimSessionsPanel/SimSessionsPanel';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, type FC } from 'react';
import { useForm } from 'react-hook-form';
import useOrganization from 'state/contexts/Organization/useOrganization';
import apiDateToDateTime from 'utils/dates/apiDateToDateTime';
import useOrgUrl from 'utils/legacy/useOrgUrl';
import SimRecentSessionsOrganization, {
  type TagSelectFormFields,
} from './SimRecentSessionsOrganization';
import styles from './SimRecentSessionsView.module.css';

interface SimRecentSessionsViewProps {
  mode: 'sim' | 'organization';
  hologramSimId?: HologramSimId;
  simDetailBaseUrl: string;
  usageByRecentSessionUrl: string;
  pageSize: number;
}

const SimRecentSessionsView: FC<SimRecentSessionsViewProps> = ({
  mode,
  hologramSimId,
  simDetailBaseUrl,
  usageByRecentSessionUrl,
  pageSize,
}) => {
  const organizationId = useOrganization();

  const {
    deviceDetails,
    isLoading: simDeviceDetailsLoading,
    error: deviceDetailsError,
  } = useSimDeviceDetails(hologramSimId);

  const {
    simBilling: { billingPeriodStart },
  } = useSimBilling(hologramSimId);

  // Tag selection
  const { register, watch, setValue } = useForm<TagSelectFormFields>({
    defaultValues: { tags: [], selectAllTags: '' },
    mode: 'onChange',
  });

  const { selectAllTags, tags } = watch();

  useEffect(() => {}, []);

  const tagsForFilter = useMemo(() => {
    if (!tags || tags.length === 0 || selectAllTags === '1') {
      return undefined;
    }

    return tags.map((tag) => parseInt(tag, 10));
  }, [selectAllTags, tags]);

  const {
    usageData,
    loadMore,
    mutate: mutateSessions,
    isValidating: usageDataRefreshing,
    end: usageDataEnd,
    isEmpty: usageDataIsEmpty,
    isLoadingMore: usageDataLoadingMore,
    isRefreshing: usageDataIsRefreshing,
    isLoading: usageDataLoading,
    error: usageDataError,
  } = useUsageData({
    deviceId: mode === 'sim' ? deviceDetails.claimedDeviceId : undefined,
    organizationId,
    pageSize,
    tagIds: tagsForFilter,
  });

  const url = useOrgUrl({ baseUrl: usageByRecentSessionUrl, orgId: organizationId });

  const latestSessionTimestamp = !usageDataIsEmpty ? usageData[0]?.timestamp : undefined;

  const showBillingPeriodWarning = useMemo(() => {
    if (!latestSessionTimestamp || !billingPeriodStart) {
      return false;
    }

    const billingStartDateTime = apiDateToDateTime(billingPeriodStart);
    const latestSessionDateTime = DateTime.fromFormat(
      latestSessionTimestamp,
      'yyyy-MM-dd HH:mm:ss',
      { zone: 'utc' }
    );

    if (!billingStartDateTime?.isValid || !latestSessionDateTime.isValid) {
      return false;
    }

    return latestSessionDateTime < billingStartDateTime;
  }, [billingPeriodStart, latestSessionTimestamp]);

  const refreshSessions = useCallback(() => {
    mutateSessions();
  }, [mutateSessions]);

  const error = usageDataError || deviceDetailsError;

  const usageDataShowLoading = usageDataRefreshing || usageDataLoadingMore || usageDataIsRefreshing;
  const contents = useMemo(() => {
    if (error) {
      return (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading recent sessions"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          We will keep trying in the background or you can try to reload the page.
        </PanelNotification>
      );
    }

    if (simDeviceDetailsLoading || usageDataLoading) {
      return <Loader />;
    }

    if (usageDataIsEmpty) {
      if (mode === 'organization') {
        if (selectAllTags || (tagsForFilter && tagsForFilter.length > 0)) {
          return (
            <PanelNotification
              icon="CircleInfo"
              label="No recent sessions found for tagged SIMs"
              actions={
                <Button to={url!} variant="tertiary" iconEnd="ArrowEastShort">
                  View session history reporting
                </Button>
              }
            >
              We haven’t seen any recent sessions in the last 90 days for SIMs with the tags you
              selected. You can find more detailed data usage history in the data usage reports.
            </PanelNotification>
          );
        }
        return (
          <PanelNotification
            icon="CircleInfo"
            label="No recent sessions could be found"
            actions={
              <Button to={url!} variant="tertiary" iconEnd="ArrowEastShort">
                View session history reporting
              </Button>
            }
          >
            We haven’t seen any recent sessions within the last 90 days for your fleet. Additional
            data usage history is available in the data usage reports.
          </PanelNotification>
        );
      }
      return (
        <PanelNotification icon="CircleInfo" label="This SIM hasn’t connected yet">
          This SIM is activated and able to connect to the Hologram network, but has not yet
          connected to a local tower or passed data.
        </PanelNotification>
      );
    }

    return (
      <>
        <SimRecentDataSessionsChartPanel
          mode={mode}
          hologramSimId={hologramSimId}
          usageData={usageData}
          onLoadMoreClick={loadMore}
          loading={usageDataShowLoading}
          hasMore={!usageDataEnd}
          simDetailBaseUrl={simDetailBaseUrl}
          pageSize={pageSize}
          usageByRecentSessionUrl={usageByRecentSessionUrl}
        />
        <SimSessionsPanel
          mode={mode}
          hologramSimId={hologramSimId}
          usageData={usageData}
          onLoadMoreClick={loadMore}
          loading={usageDataShowLoading}
          hasMore={!usageDataEnd}
          simDetailBaseUrl={simDetailBaseUrl}
          pageSize={pageSize}
          usageByRecentSessionUrl={usageByRecentSessionUrl}
        />
        <div className={styles.helptext}>
          Limited to the last 90 days of session data. Some networks report data usage in fixed time
          blocks rather than individual sessions. IMEIs are not provided for every session by every
          network.
        </div>
      </>
    );
  }, [
    error,
    simDeviceDetailsLoading,
    usageDataLoading,
    usageDataIsEmpty,
    mode,
    hologramSimId,
    usageData,
    loadMore,
    usageDataShowLoading,
    usageDataEnd,
    simDetailBaseUrl,
    pageSize,
    usageByRecentSessionUrl,
    selectAllTags,
    tagsForFilter,
    url,
  ]);

  return (
    <div className={styles.wrapper}>
      {showBillingPeriodWarning && (
        <BannerNotification variant="caution">
          The most recent sessions for this SIM were in a previous billing period.
        </BannerNotification>
      )}
      {mode === 'organization' && (
        <div className={styles.actions}>
          <SimRecentSessionsOrganization
            refreshSessions={refreshSessions}
            register={register}
            watch={watch}
            setValue={setValue}
          />
        </div>
      )}
      {contents}
    </div>
  );
};

export default SimRecentSessionsView;
